import {
    DndContext,
    DragEndEvent,
    MouseSensor,
    MouseSensorOptions,
    SensorDescriptor,
    useSensor,
    useSensors
} from '@dnd-kit/core'
import { restrictToVerticalAxis, restrictToWindowEdges } from '@dnd-kit/modifiers'
import React, { ReactElement, ReactNode, useContext, useMemo, useState } from 'react'
import uuid from 'uuid-random'
import {
    ConfigureReport as ConfigureReportLanguage,
    LanguageMainMenu,
    LanguageMyReportList,
    LanguagePrompt
} from '../../../../i18n'

// Utilities imports.
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { handleRptPortalDeleteReport } from '../../../NonReact/utils/RptPortal'
import { arrayFromMapIterator } from '../../../NonReact/utils/Utils'
// Commons imports.
import { SERVLET_COMMAND, ServletCmds } from '../../../NonReact/constants/ServletCommands'
import { IconName } from '../../Common/Components/Icon'
import IconButton, { IconButtonType } from '../../Common/Components/IconButton'
import { SERVLET_ACTION, ServletActions } from '../../../NonReact/constants/React/ServletActions/ServletActions'
// Contexts imports.
import { MessageContext, PromptContext, ServletPathContext, UserInfoContext } from '../../Common/Contexts'
import { ReactState, StateSetter, StateSetterAction } from '../../Common/Types/Types'
import { prepareReportDescriptionForAddUpdateRequest, toReportListItemsAsTree } from '../../Common/Utils/Reports'
// Utilities imports.
import ConfigureReport from '../../ConfigureReport'
import AddReportButton from './AddReportButton'
// Sub imports.
import { updateReportDescription, updateReportDescriptions } from '../../Common/API/ReportAPIClient'
import {
    matchParentFolderId,
    PrepareCopyResultMessage,
    PrepareDeletePermanentlyResultMessage,
    PrepareMoveToBinDetails,
    PrepareMoveToBinResultMessage,
    PrepareRestoreResultMessage,
    recursiveSubFolderGet
} from '../../Common/Utils/ReportList'
import { copyReport, deleteReport, moveReportToBin, restoreReportFromBin } from './API'
import PromptMessageWithWarning from './PromptMessageWithWarning'
import PromptMoveToBinMessage from './PromptMoveToBinMessage'
import ReportListItem from './ReportListItem'
import ReportRecycleBin from './ReportRecycleBin'
import {
    DraggableFolderData,
    DraggableReportData,
    DroppableData,
    ReportListItemsIndexByReportID,
    ReportListItemType
} from './Types'

import { EMPTY_STRING } from '../../../NonReact/constants/Common'
import { ReportingAccessLevels } from '../../AddUser/Reporting/ReportingAccessLevels'
import { useFeatureFlag } from '../../Common/Hooks/useFeatureFlag'
import { ReportDragDropClasses, ReportDragDropIDs, ReportDragDropIDSeparator } from './Constants'
import { ReportListWrapper } from './ReportListWrapper'
import DefaultReportMenuAdapter from './DefaultReportMenuAdapter'
import { BackendPermissionNames } from '../../Common/Constants/Permissions'
import { JSONResponse, ReportListItemResponse } from '../../../NonReact/common/Types/Types'

type Props = {
    reportListItemsAsMap: ReportListItemsIndexByReportID
    setReportListItemsAsMap: StateSetter<ReportListItemsIndexByReportID>
    hoveredItemID: Nullable<string>
    setHoveredItemID: StateSetter<Nullable<string>>
    editingItemID: Nullable<string>
    setEditingItemID: StateSetter<Nullable<string>>
    checkedItemsIDs: string[]
    setCheckedItemsIDs: StateSetter<string[]>
    selectedReportBinItemIDs: string[]
    setSelectedReportBinItemIDs: StateSetter<string[]>
    initialiseReportListData: () => Promise<void>
    refreshReportListDataAndStartReport: (rptID: string, reportDesc: string) => Promise<void>
}

/**
 * Report list component with unlimited nesting level.
 *
 * @param reportListItemsAsMap Report items as map indexed by ID.
 * @param setReportListItemsAsMap The setter for corresponding report items map.
 * @param hoveredItemID The ID of currently hovered item.
 * @param setHoveredItemID Setter for corresponding property.
 * @param editingItemID The ID of currently editing item - we support only one at the same time.
 * @param setEditingItemID The setter for corresponding property.
 * @param checkedItemsIDs Array of checked items IDs.
 * @param setCheckedItemsIDs The setter for corresponding array.
 * @param selectedReportBinItemIDs Array of checked items IDs within recycle bin.
 * @param setSelectedReportBinItemIDs The setter for corresponding array.
 * @param initialiseReportListData Report list data initialisation method.
 * @param refreshReportListDataAndStartReport Refresh report list handler.
 */
const ReportList: React.FC<Props> = ({
    reportListItemsAsMap,
    setReportListItemsAsMap,
    hoveredItemID,
    setHoveredItemID,
    editingItemID,
    setEditingItemID,
    checkedItemsIDs,
    setCheckedItemsIDs,
    selectedReportBinItemIDs,
    setSelectedReportBinItemIDs,
    initialiseReportListData,
    refreshReportListDataAndStartReport
}): ReactElement => {
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle('LanguagePrompt')
    language.loadBundle('LanguageMyReportList')
    language.loadBundle('LanguageGeneral')

    const servletPath = useContext(ServletPathContext) as string
    const { displayError, displayWarningMessage, displayErrorMessage, displaySuccessMessage } =
        useContext(MessageContext)
    const { displayPrompt } = useContext(PromptContext)
    const userInfo = useContext(UserInfoContext)
    const isCannedUser: boolean = useMemo(
        (): boolean =>
            userInfo?.Scope?.ReportingAccess === ReportingAccessLevels.ESSENTIALS ||
            userInfo?.Scope?.ReportingAccess === ReportingAccessLevels.PROFESSIONAL ||
            userInfo?.Scope?.ReportingAccess === ReportingAccessLevels.CALL_CENTRE_AGENT,
        [userInfo]
    )
    const isSignInAs: boolean = useMemo((): boolean => userInfo?.SessionInfo?.SignInAsActive ?? false, [userInfo])
    const isSignInAsActive: boolean = useMemo((): boolean => {
        if (isCannedUser) {
            return false
        }

        return isSignInAs
    }, [isCannedUser, isSignInAs])
    const isUserReportReadOnly = userInfo?.Scope?.ReportReadOnly || false

    const [selectedReportID, setSelectedReportID]: ReactState<string> = useState<string>('')
    const [isReportConfigModalOpen, setIsReportConfigModalOpen]: ReactState<boolean> = useState<boolean>(false)
    const [highlightRows, setHighlightRows]: ReactState<boolean> = useState<boolean>(false)
    const [hasHighlight, setHasHighlight]: ReactState<boolean> = useState<boolean>(false)
    const [copiedReportsIDs, setCopiedReportsIDs]: ReactState<string[]> = useState<string[]>([])

    const dndEnabled: Definable<boolean> = useFeatureFlag('reportMenuDnDEnabled')

    /**
     * Add report onclick method.
     */
    const addReport = (): void => {
        const params = new URLSearchParams()
        params.set(SERVLET_COMMAND, ServletCmds.RPT_CHANGE)
        params.set(SERVLET_ACTION, ServletActions.SHOW_ADD)

        const addReportWindow = window.open(
            `${servletPath}?${params.toString()}`,
            '1',
            'menubar=0,location=0,resizable=1,status=0,scrollbars=0,directories=0,dependent=1,innerWidth=1145px,innerHeight=553px,left=3px,top=727px'
        )

        if (addReportWindow !== null) {
            addReportWindow.onbeforeunload = (): void => {
                setTimeout((): void => {
                    initialiseReportListData()
                }, 1000)
            }
        }
    }

    /**
     * Modify report onclick method.
     */
    const modifyReport = (reportID: string): void => {
        if (reportID) {
            const params = new URLSearchParams()
            params.set(SERVLET_COMMAND, ServletCmds.RPT_CHANGE)
            params.set(SERVLET_ACTION, ServletActions.SHOW_CHG)
            params.set('ID', reportID)

            const modifyReportWindow = window.open(
                `${servletPath}?${params.toString()}`,
                '1',
                'menubar=0,location=0,resizable=1,status=0,scrollbars=0,directories=0,dependent=1,innerWidth=1145px,innerHeight=553px,left=3px,top=727px'
            )

            if (modifyReportWindow !== null) {
                modifyReportWindow.onbeforeunload = (): void => {
                    setTimeout((): void => {
                        initialiseReportListData()
                    }, 1000)
                }
            }
        }
    }

    /**
     * Modify report using new or old method.
     *
     * @param e - React mouse event.
     * @param reportID - ID of the report to modify
     */
    const setModalOpenAndSelectedReport = (e: React.MouseEvent, reportID: string): void => {
        if (e.shiftKey && e.altKey) {
            // Easter egg with old configuration window.
            modifyReport(reportID)
        } else {
            setSelectedReportID(reportID)
            setIsReportConfigModalOpen(true)
        }
    }

    /**
     * Report delete permanently click handler - deleted reports selected in recycle bin.
     */
    const handleReportDeletePermanently = async (): Promise<void> => {
        const deletedReportIds: string[] = []
        const failedReportIDs = []

        const checkedReports: ReportListItemType[] = arrayFromMapIterator(reportListItemsAsMap).filter(
            (reportItem) => reportItem.isReport && selectedReportBinItemIDs.includes(reportItem.id)
        )

        // Try to delete the reports.
        for (let index = 0; index < checkedReports.length; index += 1) {
            try {
                // eslint-disable-next-line
                const reportDeleteResponse = await deleteReport(servletPath, checkedReports[index].id)
                // Add the ID of deleted item to the temporary array so that we can then "unselect" all
                // successfully deleted items.
                deletedReportIds.push(checkedReports[index].id)
            } catch (error) {
                failedReportIDs.push(checkedReports[index].id)
            }
        }

        // Display toast message to the user.
        if (deletedReportIds.length === 0) {
            // Error
            displayErrorMessage(
                PrepareDeletePermanentlyResultMessage(language, deletedReportIds.length, checkedReports.length)
            )
        } else if (failedReportIDs.length === 0) {
            // Success
            displaySuccessMessage(
                PrepareDeletePermanentlyResultMessage(language, deletedReportIds.length, checkedReports.length)
            )
        } else if (failedReportIDs.length > 0 && deletedReportIds.length > 0) {
            // Warning
            displayWarningMessage(
                PrepareDeletePermanentlyResultMessage(language, deletedReportIds.length, checkedReports.length)
            )
        }

        // Unselect the deleted items. (Clean-up the selected items IDs list)
        setSelectedReportBinItemIDs((prevCheckedItemsIDs) =>
            prevCheckedItemsIDs.filter((rptListItemID) => !deletedReportIds.includes(rptListItemID))
        )

        // Reload report list.
        initialiseReportListData()
    }

    /**
     * Report move to bin click handler.
     */
    const handleReportMoveToBin = async (): Promise<void> => {
        const binnedReportIDs: string[] = []
        const failedReportIDs = []

        const checkedReports: ReportListItemType[] = arrayFromMapIterator(reportListItemsAsMap).filter(
            (reportItem): boolean => reportItem.isReport && checkedItemsIDs.includes(reportItem.id)
        )
        for (let index = 0; index < checkedReports.length; index += 1) {
            try {
                // eslint-disable-next-line
                await moveReportToBin(servletPath, checkedReports[index].id)
                // Delete report from view inside the report portal.
                handleRptPortalDeleteReport(checkedReports[index].id)
                // Add the ID of deleted item to the temporary array so that we can then "unselect" all
                // successfully deleted items.
                binnedReportIDs.push(checkedReports[index].id)
            } catch (error) {
                failedReportIDs.push(checkedReports[index].id)
            }
        }
        // Display toast message to the user.
        if (binnedReportIDs.length === 0) {
            // Error
            displayErrorMessage(PrepareMoveToBinResultMessage(language, binnedReportIDs.length, checkedReports.length))
        } else if (failedReportIDs.length === 0) {
            // Success
            displaySuccessMessage(
                PrepareMoveToBinResultMessage(language, binnedReportIDs.length, checkedReports.length),
                PrepareMoveToBinDetails(checkedReports)
            )
        } else if (failedReportIDs.length > 0 && binnedReportIDs.length > 0) {
            // Warning
            displayWarningMessage(
                PrepareMoveToBinResultMessage(language, binnedReportIDs.length, checkedReports.length)
            )
        }

        // Unselect the restored items. (Clean-up the selected items IDs list)
        setCheckedItemsIDs((prevCheckedItemsIDs): string[] =>
            prevCheckedItemsIDs.filter((rptListItemID): boolean => !binnedReportIDs.includes(rptListItemID))
        )
        // Reload report list.
        initialiseReportListData()
    }

    /**
     * Prompt popup warning before moving reports to bin.
     */
    const displayPromptOnMoveToBinReports = (): void => {
        displayPrompt(
            <PromptMoveToBinMessage />,
            `${language?.get(LanguagePrompt.LanguagePrompt_PromptConfirm)}`,
            `${language?.get(LanguagePrompt.LanguagePrompt_PromptCancel)}`,
            () => {
                handleReportMoveToBin()
            },
            undefined,
            undefined,
            undefined
        )
    }

    /**
     * Prompt popup warning before deletion of reports.
     */
    const displayPromptOnDeleteReports = (): void => {
        displayPrompt(
            <PromptMessageWithWarning
                message={language?.get(LanguagePrompt.PromptDeletereports)}
                warningMessage={language?.get(LanguagePrompt.PromptReportswillbedeletedpermanently)}
            />,
            `${language?.get(LanguagePrompt.LanguagePrompt_PromptConfirm)}`,
            `${language?.get(LanguagePrompt.LanguagePrompt_PromptCancel)}`,
            (): Promise<void> => handleReportDeletePermanently(),
            undefined,
            undefined,
            undefined
        )
    }

    /**
     * Report move to bin click handler.
     */
    const handleReportsRestore = async (): Promise<void> => {
        const restoredReportIDs: string[] = []
        const failedReportIDs = []

        const checkedReports: ReportListItemType[] = arrayFromMapIterator(reportListItemsAsMap).filter(
            (reportItem): boolean => reportItem.isReport && selectedReportBinItemIDs.includes(reportItem.id)
        )

        for (let index = 0; index < checkedReports.length; index += 1) {
            try {
                // eslint-disable-next-line
                const reportDeleteResponse = await restoreReportFromBin(servletPath, checkedReports[index].id)
                // Add the ID of deleted item to the temporary array so that we can then "unselect" all
                // successfully deleted items.
                restoredReportIDs.push(checkedReports[index].id)
                // Delete report from view inside the report portal.
                handleRptPortalDeleteReport(checkedReports[index].id)
            } catch (error) {
                failedReportIDs.push(checkedReports[index].id)
            }
        }

        if (restoredReportIDs.length === 0) {
            // Error
            displayErrorMessage(PrepareRestoreResultMessage(language, restoredReportIDs.length, checkedReports.length))
        } else if (failedReportIDs.length === 0) {
            // Success
            displaySuccessMessage(
                PrepareRestoreResultMessage(language, restoredReportIDs.length, checkedReports.length)
            )
        } else if (failedReportIDs.length > 0 && restoredReportIDs.length > 0) {
            // Warning
            displayWarningMessage(
                PrepareRestoreResultMessage(language, restoredReportIDs.length, checkedReports.length)
            )
        }

        // Unselect the deleted items. (Clean-up the selected items IDs list)
        setSelectedReportBinItemIDs((prevCheckedItemsIDs): string[] =>
            prevCheckedItemsIDs.filter((rptListItemID): boolean => !restoredReportIDs.includes(rptListItemID))
        )
        // Reload report list.
        initialiseReportListData()
    }

    /**
     * This function is invoked when copy is initialised from the single report interface.
     *
     * @param reportID ID of the report to copy.
     */
    const handleSingleReportCopy = async (reportID: string): Promise<void> =>
        copyReport(reportID)
            .then((): void => {
                displaySuccessMessage(language.get(LanguageMyReportList.ReportlistReportwascopied))
                initialiseReportListData()
            })
            .catch(displayError)

    const findAllReportsInFolderRecursively = (folderItem: ReportListItemType): string[] => {
        let reportIDsToDelete: string[] = []
        if (folderItem.subItems?.length) {
            folderItem.subItems?.forEach((item: ReportListItemType): void => {
                if (item.isReport) {
                    reportIDsToDelete.push(item.id)
                } else {
                    reportIDsToDelete = [...reportIDsToDelete, ...findAllReportsInFolderRecursively(item)]
                }
            })
        }
        return reportIDsToDelete
    }

    /**
     * Unchecked folder sub items.
     *
     * @param folderID - ID of the folder to uncheck sub items for.
     * @param reportIDsToUncheck - List of report IDs to uncheck if they are in folders.
     */
    const uncheckFolderSubItems = (folderID: string, reportIDsToUncheck?: string[]): void => {
        // Get a list of all of the report list items.
        const reportList: ReportListItemType[] = Array.from(reportListItemsAsMap.values())
        // List of folder IDs.
        const folders: string[] = recursiveSubFolderGet(reportList, [folderID])

        if (reportIDsToUncheck) {
            // Uncheck only provided list of reportIDs within the folders.
            const reportItemIDsToUncheck = reportList
                // Find the items in this folder
                .filter(
                    (currentReportListItem: ReportListItemType) =>
                        matchParentFolderId(currentReportListItem, folders) &&
                        currentReportListItem.isReport &&
                        reportIDsToUncheck.includes(currentReportListItem.id)
                )
                .map((currentReportListItem: ReportListItemType): string => currentReportListItem.id)

            setCheckedItemsIDs((prevCheckedItemsIDs: string[]) => {
                const newListOfCheckedItemIDs = prevCheckedItemsIDs.filter((prevCheckedItemID): boolean =>
                    reportItemIDsToUncheck.includes(prevCheckedItemID)
                )

                return newListOfCheckedItemIDs
            })
        } else {
            // When folder is collapsed uncheck all selected options.
            reportList
                // Find the items in this folder
                .filter(
                    (currentReportListItem: ReportListItemType) =>
                        matchParentFolderId(currentReportListItem, folders) && currentReportListItem.isReport
                )
                .forEach((currentReportListItem: ReportListItemType) => {
                    // Uncheck every item
                    setCheckedItemsIDs((prevCheckedItemsIDs: string[]) => {
                        if (checkedItemsIDs.includes(currentReportListItem.id)) {
                            return prevCheckedItemsIDs.filter(
                                (itemID: string): boolean => itemID !== currentReportListItem.id
                            )
                        }

                        return prevCheckedItemsIDs
                    })
                })
        }
    }

    /**
     * Actually perform the action of moving folder contents to the bin.
     *
     * @param folderItem List of report IDs which are inside of this folder and which needs to be moved into bin.
     */
    const handleFolderMoveToBin = async (folderItem: ReportListItemType): Promise<void> => {
        const reportIDsToDelete: string[] = findAllReportsInFolderRecursively(folderItem)
        const deletedReportIDs: string[] = []

        const failedReportIDs: string[] = []

        for (let index: number = 0; index < reportIDsToDelete.length; index++) {
            const reportIDToDelete: string = reportIDsToDelete[index]
            try {
                // eslint-disable-next-line
                await moveReportToBin(servletPath, reportIDToDelete)

                deletedReportIDs.push(reportIDToDelete)
            } catch (error) {
                failedReportIDs.push(reportIDToDelete)
            }
        }

        if (deletedReportIDs.length === 0) {
            // Error
            displayErrorMessage(
                PrepareMoveToBinResultMessage(language, deletedReportIDs.length, reportIDsToDelete.length)
            )
        } else if (failedReportIDs.length === 0) {
            // Success
            displaySuccessMessage(
                PrepareMoveToBinResultMessage(language, deletedReportIDs.length, reportIDsToDelete.length),
                language.get(LanguageMyReportList.ReportlistThirtydaysremaining)
            )
        } else if (failedReportIDs.length > 0 && deletedReportIDs.length > 0) {
            // Warning
            displayWarningMessage(
                PrepareMoveToBinResultMessage(language, deletedReportIDs.length, reportIDsToDelete.length)
            )
        }

        uncheckFolderSubItems(folderItem.id, deletedReportIDs)

        initialiseReportListData()
    }

    /**
     * Report copy click handler.
     */
    const handleReportCopy = async (copyPaste: boolean = false): Promise<void> => {
        // Determine if the duplicated reports are copied from the clipboard or from the checked items.
        const detectSourceReport = (id: string): boolean =>
            copyPaste ? copiedReportsIDs.includes(id) : checkedItemsIDs.includes(id)

        const checkedReports: ReportListItemType[] = arrayFromMapIterator(reportListItemsAsMap).filter(
            (reportItem: ReportListItemType): boolean => reportItem.isReport && detectSourceReport(reportItem.id)
        )

        const copiedReportIDs: string[] = []
        const failedReportIDs: string[] = []

        let lastSuccessfulReportID: Nullable<string> = null

        Promise.all(
            checkedReports.map(
                (reportItem: ReportListItemType): Promise<void> =>
                    copyReport(reportItem.id)
                        .then((reportCopyResponse: JSONResponse<ReportListItemResponse>): void => {
                            if (reportCopyResponse) {
                                lastSuccessfulReportID = reportCopyResponse.ID
                                copiedReportIDs.push(reportCopyResponse.ID)
                            }
                        })
                        .catch((error: Error): void => {
                            displayError(error)
                            failedReportIDs.push(reportItem.id)
                        })
            )
        ).then((): void => {
            // Display toast message to the user.
            if (copiedReportIDs.length === 0) {
                // Error
                displayErrorMessage(PrepareCopyResultMessage(language, copiedReportIDs.length, checkedReports.length))
            } else if (failedReportIDs.length === 0) {
                // Success
                displaySuccessMessage(PrepareCopyResultMessage(language, copiedReportIDs.length, checkedReports.length))
            } else if (failedReportIDs.length > 0 && copiedReportIDs.length > 0) {
                // Warning
                displayWarningMessage(PrepareCopyResultMessage(language, copiedReportIDs.length, checkedReports.length))
            }

            initialiseReportListData()
                .then((): void => {
                    // Select last successful report after copy.
                    if (lastSuccessfulReportID) {
                        // Note that after copy we have to unselect all items that were selected before except the folders
                        // that should be still open. Then we have to select the last successfully copied item.
                        setCheckedItemsIDs((prevCheckedItemsIDs) => [
                            ...prevCheckedItemsIDs.filter((rptListItemID) => {
                                const rptListItem = reportListItemsAsMap.get(rptListItemID)
                                return rptListItem && !rptListItem.isReport
                            }),
                            lastSuccessfulReportID as string
                        ])
                    }
                })
                .catch(displayError)
        })
    }

    /**
     * Setter for report list checked item IDs - wrapper method for state setter that unselects reports in bin.
     */
    const setReportListCheckedItemIDs = (setCheckedIDs: StateSetterAction<string[]>): void => {
        setCheckedItemsIDs(setCheckedIDs)

        setSelectedReportBinItemIDs((previousSelectedReportBinItems): string[] =>
            arrayFromMapIterator(reportListItemsAsMap)
                .filter(
                    (rptListItem: ReportListItemType): boolean =>
                        !rptListItem.isReport && previousSelectedReportBinItems.includes(rptListItem.id)
                )
                .map((reportListItem: ReportListItemType): string => reportListItem.id)
        )
    }

    /**
     * Setter for binned report list checked item IDs - wrapper method for state setter that unselects reports not in bin.
     */
    const setReportBinCheckedItemIDs = (setCheckedIDs: StateSetterAction<string[]>): void => {
        setSelectedReportBinItemIDs(setCheckedIDs)
        setCheckedItemsIDs((previousCheckedItemsIDs): string[] =>
            arrayFromMapIterator(reportListItemsAsMap)
                .filter(
                    (rptListItem: ReportListItemType) =>
                        !rptListItem.isReport && previousCheckedItemsIDs.includes(rptListItem.id)
                )
                .map((reportListItem: ReportListItemType) => reportListItem.id)
        )
    }

    const handleReportDelete = async (reportID: string): Promise<void> => {
        let isError = false
        try {
            // eslint-disable-next-line
            await moveReportToBin(servletPath, reportID)
            // Delete report from view inside the report portal.
            handleRptPortalDeleteReport(reportID)
        } catch (error) {
            displayError(error as Error)
            isError = true
        }

        if (!isError) {
            displaySuccessMessage(
                language.get(LanguageMyReportList.ReportlistReportwasmovedtobin),
                language.get(LanguageMyReportList.ReportlistThirtydaysremaining)
            )

            if (checkedItemsIDs.indexOf(reportID) > -1) {
                setCheckedItemsIDs((prevCheckedItemIDs) =>
                    prevCheckedItemIDs.filter((prevCheckedItemID) => prevCheckedItemID !== reportID)
                )
            }
        }

        initialiseReportListData()
    }

    /**
     * Builds menu items converted to react elements.
     */
    const getReportListItems = (): ReactNode => {
        if (reportListItemsAsMap.size > 0) {
            const reportListItemsAsArray: ReportListItemType[] = arrayFromMapIterator(reportListItemsAsMap)
            const reportListItemsAsArrayNotDeleted: ReportListItemType[] = reportListItemsAsArray.filter(
                (reportListItem): boolean => !reportListItem.isBinned
            )

            const reportListItemsAsTree: ReportListItemType[] = toReportListItemsAsTree(
                reportListItemsAsArrayNotDeleted,
                undefined
            )

            return reportListItemsAsTree.map((value) => (
                <ReportListItem
                    key={value.id}
                    reportListItem={value}
                    isReportWriteAllowed={!isUserReportReadOnly || isSignInAsActive}
                    hoveredItemID={hoveredItemID}
                    setHoveredItemID={setHoveredItemID}
                    editingItemID={editingItemID}
                    setEditingItemID={setEditingItemID}
                    checkedItemsIDs={checkedItemsIDs}
                    setCheckedItemsIDs={setReportListCheckedItemIDs}
                    reportListItemsAsMap={reportListItemsAsMap}
                    setReportListItemsAsMap={setReportListItemsAsMap}
                    initialiseReportListData={initialiseReportListData}
                    setHasHighlight={setHasHighlight}
                    modifyReport={setModalOpenAndSelectedReport}
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    copyReport={handleSingleReportCopy}
                    isParentFolderHighlighted={false}
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    handleReportDelete={handleReportDelete}
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    handleFolderMoveToBin={handleFolderMoveToBin}
                    dndDisabled={!dndEnabled}
                />
            ))
        }
        return <div className='no-reports-text'>{language.get(LanguageMyReportList.NoreportsconfiguredText)}</div>
    }

    /**
     * Method to find the reports are already exist or not
     * @returns true if report exist otherwise false
     */
    const isReportExist = (): boolean =>
        arrayFromMapIterator(reportListItemsAsMap ?? {}).some(
            (reportListItem: ReportListItemType): boolean => !reportListItem.isBinned
        )

    const highlightSelectedRows = (): void => setHighlightRows(true)
    const unhighlightSelectedRows = (): void => setHighlightRows(false)

    // Resolve all checked reports.
    const reportsCheckedAll = arrayFromMapIterator(reportListItemsAsMap).filter(
        (rptListItem) => rptListItem.isReport && checkedItemsIDs.includes(rptListItem.id)
    )
    const reportsCheckedCount = reportsCheckedAll.length

    // Resolve all checked reports.
    const binnedReportsCheckedAll = arrayFromMapIterator(reportListItemsAsMap).filter(
        (rptListItem) => rptListItem.isReport && selectedReportBinItemIDs.includes(rptListItem.id)
    )
    const binnedReportsCheckedCount = binnedReportsCheckedAll.length

    /**
     * Pluralise the word reports for title tooltips.
     */
    const pluraliseReports = (title: string, reportsCount: number): string => `${title}${reportsCount > 1 ? 's' : ''}`

    // Sesnsors for dnd kit to restrict behaviour.
    const mouseSensor: SensorDescriptor<MouseSensorOptions> = useSensor(MouseSensor, {
        activationConstraint: {
            distance: 5
        }
    } as MouseSensorOptions)

    const sensors = useSensors(mouseSensor)

    const flatenReportSubItems = (reportListItems: ReportListItemType[]): ReportListItemType[] => {
        const reportListItemsAsArray: ReportListItemType[] = []

        reportListItems.forEach((reportListItem: ReportListItemType): void => {
            reportListItemsAsArray.push(reportListItem)
            if (reportListItem.subItems) {
                reportListItemsAsArray.push(...flatenReportSubItems(reportListItem.subItems))
            }
        })

        return reportListItemsAsArray
    }

    /**
     * It takes a DragEndEvent and returns the draggable ID
     * @param {DragEndEvent} event - DragEndEvent - this is the event that is passed to the onDragEnd
     * function.
     */
    const getDraggableID = (event: DragEndEvent): string =>
        event.active?.id.toString().split(ReportDragDropIDSeparator)[0] ?? EMPTY_STRING

    /**
     * > Get the droppable ID from the event
     * @param {DragEndEvent} event - DragEndEvent - The event that is triggered when the drag ends.
     */
    const getDroppableID = (event: DragEndEvent): string =>
        event.over?.id.toString().split(ReportDragDropIDSeparator)[0] ?? EMPTY_STRING

    /**
     * It takes a DragEndEvent and returns a DraggableFolderData
     * @param {DragEndEvent} event - DragEndEvent - The event that is triggered when the draggable
     * element is dropped.
     */
    const getDraggableFolderData = (event: DragEndEvent): DraggableFolderData =>
        event?.active?.data?.current as DraggableFolderData

    /**
     * It takes a DragEndEvent and returns a DraggableReportData
     * @param {DragEndEvent} event - DragEndEvent - The event that is triggered when the draggable is
     * dropped.
     */
    const getDraggableReportData = (event: DragEndEvent): DraggableReportData =>
        event?.active?.data?.current as DraggableReportData

    /**
     * It takes a DragEndEvent and returns the DroppableData of the element that the dragged element
     * was dropped on
     * @param {DragEndEvent} event - DragEndEvent - The event object that is passed to the onDragEnd
     * function.
     */
    const getDroppableData = (event: DragEndEvent): DroppableData => event?.over?.data?.current as DroppableData

    /**
     * It takes a drag event, and depending on the draggable and droppable IDs, it either moves a
     * report or folder to the root, bin, or another folder
     * @param {DragEndEvent} event - DragEndEvent
     * @returns The return type is a function that takes in a DragEndEvent and returns void.
     */
    const handleDragEnd = (event: DragEndEvent): void => {
        if (!event.over) return // No drop target

        /* Getting the draggableId from the event. */
        const draggableId: string = getDraggableID(event)

        /* Getting the droppable ID from the event. */
        const droppableId: string = getDroppableID(event)

        switch (droppableId) {
            // droppable container is the root
            case ReportDragDropIDs.DROPPABLE_ROOT:
                // draggable is a folder
                if (draggableId === ReportDragDropIDs.DRAGGABLE_FOLDER) {
                    const folderData: DraggableFolderData = getDraggableFolderData(event)

                    if (folderData.parentFolderID === undefined) return

                    const subItems: ReportListItemType[] = folderData.subItems ?? []
                    const reportFromFolder: ReportListItemType[] = flatenReportSubItems(subItems)

                    if (subItems.length > 0) {
                        updateReportDescriptions(
                            reportFromFolder.filter((report: ReportListItemType): boolean => report.isReport),
                            EMPTY_STRING,
                            folderData.name,
                            folderData.parentFolderID ?? EMPTY_STRING,
                            language
                        ).catch((error: Error) => displayError(error))
                    }
                    // draggable is a report
                } else if (draggableId === ReportDragDropIDs.DRAGGABLE_REPORT) {
                    const folderData: DraggableFolderData = getDraggableFolderData(event)
                    const reportData: DraggableReportData = getDraggableReportData(event)

                    if (folderData.parentFolderID === undefined) return

                    updateReportDescription(
                        reportData.id,
                        prepareReportDescriptionForAddUpdateRequest(
                            EMPTY_STRING,
                            event?.active?.data?.current?.name as string
                        ),
                        language
                    ).catch((error: Error) => displayError(error))
                } else {
                    // do nothing
                }
                break
            // droppable container is the bin
            case ReportDragDropIDs.DROPPABLE_BIN:
                // draggable is a folder
                if (draggableId === ReportDragDropIDs.DRAGGABLE_FOLDER) {
                    const folderData: DraggableFolderData = getDraggableFolderData(event)
                    handleFolderMoveToBin(folderData.reportListItem)

                    // draggable is a report
                } else if (draggableId === ReportDragDropIDs.DRAGGABLE_REPORT) {
                    handleReportDelete(event?.active?.id.toString().split(ReportDragDropIDSeparator)[1])
                } else {
                    // do nothing
                }
                break
            // droppable container is a folder
            case ReportDragDropIDs.DROPPABLE_FOLDER:
                // draggable is a folder
                if (draggableId === ReportDragDropIDs.DRAGGABLE_FOLDER) {
                    const folderData: DraggableFolderData = getDraggableFolderData(event)
                    const droppableData: DroppableData = getDroppableData(event)

                    if (folderData.parentFolderID === droppableData.id) return

                    const subItems: ReportListItemType[] = folderData.subItems ?? []
                    const reportFromFolder: ReportListItemType[] = flatenReportSubItems(subItems)

                    if (subItems.length > 0) {
                        updateReportDescriptions(
                            reportFromFolder.filter((report: ReportListItemType): boolean => report.isReport),
                            droppableData.id,
                            folderData.name,
                            folderData.parentFolderID ?? EMPTY_STRING,
                            language
                        ).catch((error: Error) => displayError(error))
                    }

                    // draggable is a report
                } else if (draggableId === ReportDragDropIDs.DRAGGABLE_REPORT) {
                    const droppableData: DroppableData = getDroppableData(event)
                    const draggableReportData: DraggableReportData = getDraggableReportData(event)

                    if (droppableData.id === draggableReportData.parentFolderID) return

                    updateReportDescription(
                        draggableReportData.id,
                        prepareReportDescriptionForAddUpdateRequest(droppableData.id, draggableReportData.name),
                        language
                    ).catch((error: Error) => displayError(error))
                } else {
                    // do nothing
                }

                break
            default:
                break
        }
    }

    const isReportTemplateUser: boolean = !!userInfo?.Permissions?.NonAdmin?.includes(
        BackendPermissionNames.REPORT_TEMPLATE_USER
    )

    const handleKeyDownRoot = (event: React.KeyboardEvent): void => {
        if (event.ctrlKey && dndEnabled) {
            switch (event.key) {
                case `Delete`:
                    binnedReportsCheckedCount === 0 ? handleReportMoveToBin() : displayPromptOnDeleteReports()
                    break
                case 'c':
                    setCopiedReportsIDs(checkedItemsIDs)
                    displaySuccessMessage(
                        language.getInterpolated(
                            LanguageMyReportList.ReportListCopyReportsSuccessfulMessage,
                            checkedItemsIDs.filter((id: string): boolean => id.includes(':')).length
                        )
                    )
                    break
                case 'v':
                    handleReportCopy(true)
                    break
                default:
                    break
            }
        }
    }

    return (
        <div className={`${ReportDragDropClasses.DND_CONTAINER}`} id='idMyReportList'>
            <DndContext
                autoScroll={false}
                sensors={sensors}
                modifiers={[restrictToVerticalAxis, restrictToWindowEdges]}
                onDragEnd={handleDragEnd}
            >
                <ReportListWrapper
                    getReportListItems={getReportListItems}
                    hasHighlight={hasHighlight}
                    highlightRows={highlightRows}
                    handleKeyDownRoot={handleKeyDownRoot}
                    dndDisabled={!dndEnabled}
                />

                {(!isUserReportReadOnly || isSignInAsActive) && (
                    <ReportRecycleBin
                        reportListItemsAsMap={reportListItemsAsMap}
                        setReportListItemsAsMap={setReportListItemsAsMap}
                        hoveredItemID={hoveredItemID}
                        setHoveredItemID={setHoveredItemID}
                        checkedReportBinItemsIDs={selectedReportBinItemIDs}
                        setCheckedReportBinItemsIDs={setReportBinCheckedItemIDs}
                        initialiseReportListData={initialiseReportListData}
                        setHasHighlight={setHasHighlight}
                        hasHighlight={hasHighlight}
                        highlightRows={highlightRows}
                        handleKeyDownRoot={handleKeyDownRoot}
                    />
                )}
            </DndContext>

            {(!isUserReportReadOnly || isSignInAsActive) && (
                <div className='report-list-footer-wrapper'>
                    <div className='basic-actions'>
                        {/* Add report button. */}
                        <AddReportButton
                            refreshReportListDataAndStartReport={refreshReportListDataAndStartReport}
                            addReport={addReport}
                        />
                        {/* Add default report button. */}
                        <DefaultReportMenuAdapter
                            isReportExist={isReportExist()}
                            initialiseReportListData={initialiseReportListData}
                            isReportTemplateUser={isReportTemplateUser}
                        />
                    </div>
                    <div className='multi-report-actions'>
                        {/* Copy report button */}
                        {binnedReportsCheckedCount === 0 ? (
                            <IconButton
                                type={IconButtonType.BUTTON}
                                iconName={IconName.COPY}
                                title={pluraliseReports(
                                    language?.get(LanguageMyReportList.ReportlistCopyreport),
                                    reportsCheckedCount
                                )}
                                disabledSubTitle={language?.get(LanguageMainMenu.MainmenuButtonSubTitleDisabled)}
                                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                                onClick={(): Promise<void> => handleReportCopy(false)}
                                isEnabled={reportsCheckedCount > 0}
                                onMouseOver={highlightSelectedRows}
                                onMouseOut={unhighlightSelectedRows}
                            />
                        ) : (
                            <IconButton
                                type={IconButtonType.BUTTON}
                                iconName={IconName.RESTORE_FROM_TRASH}
                                title={pluraliseReports(
                                    language?.get(LanguageMyReportList.ReportlistRestorereport),
                                    selectedReportBinItemIDs.length
                                )}
                                disabledSubTitle={language?.get(LanguageMainMenu.MainmenuButtonSubTitleDisabled)}
                                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                                onClick={handleReportsRestore}
                                isEnabled={binnedReportsCheckedCount > 0}
                                onMouseOver={highlightSelectedRows}
                                onMouseOut={unhighlightSelectedRows}
                            />
                        )}
                        {/* Delete report button. */}
                        {binnedReportsCheckedCount === 0 ? (
                            <IconButton
                                type={IconButtonType.BUTTON}
                                iconName={IconName.DELETE}
                                title={pluraliseReports(
                                    language?.get(LanguageMainMenu.MainmenuButtonTitleDeletereport),
                                    reportsCheckedCount
                                )}
                                disabledSubTitle={language?.get(LanguageMainMenu.MainmenuButtonSubTitleDisabled)}
                                onClick={displayPromptOnMoveToBinReports}
                                isEnabled={reportsCheckedCount > 0}
                                onMouseOver={highlightSelectedRows}
                                onMouseOut={unhighlightSelectedRows}
                            />
                        ) : (
                            <IconButton
                                type={IconButtonType.BUTTON}
                                iconName={IconName.DELETE_FOREVER}
                                title={pluraliseReports(
                                    language?.get(LanguageMainMenu.MainmenuButtonTitlePermanentlyDeletereport),
                                    selectedReportBinItemIDs.length
                                )}
                                disabledSubTitle={language?.get(LanguageMainMenu.MainmenuButtonSubTitleDisabled)}
                                onClick={displayPromptOnDeleteReports}
                                isEnabled={binnedReportsCheckedCount > 0}
                                onMouseOver={highlightSelectedRows}
                                onMouseOut={unhighlightSelectedRows}
                            />
                        )}
                    </div>

                    {/* Configure Report popup */}
                    <ConfigureReport
                        uuid={uuid()}
                        modalTitle={language.get(ConfigureReportLanguage.ModalTitleModify)}
                        isModalOpen={isReportConfigModalOpen}
                        setIsModalOpen={setIsReportConfigModalOpen}
                        reportID={selectedReportID}
                    />
                </div>
            )}
        </div>
    )
}

export default ReportList
